import React, { useState } from "react";

import { Button } from "@lattice/frontend/components/kit/Button";
import { Surface } from "@lattice/frontend/components/kit/Surface";
import { TextField } from "@lattice/frontend/components/kit/TextField";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import {
  SettingsPanelError,
  SettingsPanelLoading,
  SettingsPanelSuccess,
  SettingsSection,
} from "./SettingsPanelState";

export type T_ReferralsPanelViewProps = {
  error: string | null;
  initialRedemptionCode: string;
  onCopyCode: () => void;
  onCopyLink: () => void;
  onRedeem: (code: string) => void;
  promoCode: string | null;
  redeeming: boolean;
  redeemed: boolean;
  referredCount: number;
};

export function ReferralsPanelView({
  error,
  initialRedemptionCode,
  onCopyCode,
  onCopyLink,
  onRedeem,
  promoCode,
  redeeming,
  redeemed,
  referredCount,
}: T_ReferralsPanelViewProps) {
  const [redemptionCode, setRedemptionCode] = useState(initialRedemptionCode);

  return (
    <div className="shell-settings-form">
      {promoCode ? (
        <SettingsSection label="Your referral">
          <Surface tone="subtle">
            <p className="shell-settings-referral__code">{promoCode}</p>
            <p className="shell-settings__body">
              {referredCount}{" "}
              {referredCount === 1 ? "person has" : "people have"} joined with
              your code.
            </p>
            <div className="shell-settings-actions">
              <Button label="Copy code" onClick={onCopyCode} size="compact" />
              <Button
                label="Copy link"
                onClick={onCopyLink}
                size="compact"
                variant="secondary"
              />
            </div>
          </Surface>
        </SettingsSection>
      ) : null}

      <SettingsSection label="Redeem a code">
        <TextField
          autoComplete="off"
          label="Referral code"
          onChange={(event) => setRedemptionCode(event.target.value)}
          placeholder="Enter code"
          value={redemptionCode}
        />
        <Button
          disabled={redemptionCode.trim().length === 0}
          label="Redeem code"
          loading={redeeming}
          onClick={() => onRedeem(redemptionCode.trim())}
        />
      </SettingsSection>
      {error ? <SettingsPanelError message={error} /> : null}
      {redeemed ? (
        <SettingsPanelSuccess message="Referral code redeemed." />
      ) : null}
    </div>
  );
}

export function ReferralsPanel({
  initialRedemptionCode,
}: {
  initialRedemptionCode: string | null;
}) {
  const utils = trpcApi.useUtils();
  const currentUser = trpcApi.users.get_current_user.useQuery({});
  const redeemReferral = trpcApi.users.redeem_referral.useMutation({
    onSuccess: async () => {
      await utils.users.get_current_user.invalidate();
    },
  });

  if (currentUser.isPending) {
    return <SettingsPanelLoading label="Loading referrals" />;
  }
  if (currentUser.isError || !currentUser.data) {
    return <SettingsPanelError message="Referrals could not be loaded." />;
  }

  const promoCode = currentUser.data.ReferralPromo?.code ?? null;
  const copyText = (value: string) => {
    void navigator.clipboard.writeText(value);
  };

  return (
    <ReferralsPanelView
      error={redeemReferral.error?.message ?? null}
      initialRedemptionCode={initialRedemptionCode ?? ""}
      onCopyCode={() => {
        if (promoCode) copyText(promoCode);
      }}
      onCopyLink={() => {
        if (promoCode) {
          copyText(`${window.location.origin}?referral=${promoCode}`);
        }
      }}
      onRedeem={(referral_code) => redeemReferral.mutate({ referral_code })}
      promoCode={promoCode}
      redeemed={redeemReferral.isSuccess}
      redeeming={redeemReferral.isPending}
      referredCount={currentUser.data.user_referred}
    />
  );
}
